import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  TextField,
  IconButton,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import headerLogo from "assets/header-logo-1.png";

import {
  getNewMessageCount,
  getPracticeProviderMobileUsers,
  getUserMessages,
  markMessagesAsRead,
  sendUserMessage,
} from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";
import ProviderList from "components/UI/scriberChat/providerList";
import ChatInput from "components/UI/scriberChat/chatinput";
import ChatMessageList from "components/UI/scriberChat/chatMessageList";
import {
  Close,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search,
} from "@mui/icons-material";

const ScriberChatting = () => {
  const userData = useUserData();
  console.log("userData: ", userData);
  const token = userData?.access_token;
  const theme = useTheme();

  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [matchedIndexes, setMatchedIndexes] = useState([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [messageCounts, setMessageCounts] = useState({});
  const chatContainerRef = useRef(null);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const showSnack = (message, severity = "error") => {
    setSnack({ open: true, message, severity });
  };

  useEffect(() => {
    if (!chatContainerRef.current) return;

    // Don't auto-scroll while searching
    if (isSearching && searchTerm) return;

    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setMatchedIndexes([]);
      setActiveMatchIndex(null);
      return;
    }

    const lower = searchTerm.toLowerCase();

    const matches = messages
      .map((msg, index) =>
        msg.message_content?.toLowerCase().includes(lower) ? index : null,
      )
      .filter((v) => v !== null);

    setMatchedIndexes(matches);
    setActiveMatchIndex(matches.length > 0 ? 0 : null);
  }, [searchTerm, messages]);

  useEffect(() => {
    if (userData?.selected_practice) {
      fetchProviders();
      getMessageCount();
    }
  }, [userData?.selected_practice]);

  const getMessageCount = async () => {
    try {
      const payload = {
        practice_id: userData.selected_practice,
        scribe_user_id: userData.email,
      };

      const res = await getNewMessageCount(payload, token);

      if (!Array.isArray(res)) return;

      const map = {};

      res.forEach((item) => {
        map[item.provider_user_id] = item.count;
      });

      setMessageCounts(map);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const markAsRead = async (provider) => {
    try {
      const payload = {
        practice_id: userData.selected_practice,
        scribe_user_id: userData.email,
        provider_user_id: provider.mobile_username,
      };
      await markMessagesAsRead(payload, token);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  const fetchProviders = async () => {
    setLoadingProviders(true);

    try {
      const payload = { practice_id: userData.selected_practice };
      const res = await getPracticeProviderMobileUsers(payload, token);

      let providerList = Array.isArray(res) ? res : [];

      const isProviderUser =
        userData?.user_details?.user_role?.toLowerCase() === "provider";

      if (isProviderUser) {
        const loggedInNpi = String(userData?.user_details?.npi || "").trim();
        const loggedInEmail = String(userData?.email || "")
          .trim()
          .toLowerCase();

        providerList = providerList.filter((provider) => {
          const providerNpi = String(provider?.npi || "").trim();
          const providerEmail = String(provider?.mobile_username || "")
            .trim()
            .toLowerCase();

          return providerNpi === loggedInNpi || providerEmail === loggedInEmail;
        });
      }

      setProviders(providerList);

      if (providerList.length === 1) {
        handleProviderSelect(providerList[0]);
      }
    } catch (e) {
      console.error(e);
      showSnack("Failed to load providers");
    } finally {
      setLoadingProviders(false);
    }
  };

  const fetchMessages = async (provider) => {
    setLoadingMessages(true);
    try {
      const payload = {
        practice_id: userData.selected_practice,
        scribe_user_id: userData.email,
        provider_user_id: provider.mobile_username,
      };

      const res = await getUserMessages(payload, token);
      const list = Array.isArray(res) ? res : res || [];

      const transformed = list.map((msg) => {
        const isMine =
          msg.sender_id?.toLowerCase() === userData.email?.toLowerCase();

        const senderType =
          msg.sender_id === provider.mobile_username ? "doctor" : "scribe";

        return {
          id: msg.id,
          message_content: msg.message_content,
          isMine,
          senderType,
          status: msg.status || "sent",
          created_date: msg.created_date,

          senderName: isMine
            ? "You"
            : senderType === "doctor"
              ? `${msg.provider_first_name} ${msg.provider_last_name}`
              : msg.scribe_first_name
                ? `${msg.scribe_first_name} ${msg.scribe_last_name?.[0] || ""}`
                : "",
        };
      });

      transformed.sort(
        (a, b) => new Date(a.created_date) - new Date(b.created_date),
      );

      setMessages(transformed);
    } catch (e) {
      console.error(e);
      showSnack("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    fetchMessages(provider);
    markAsRead(provider);

    setMessageCounts((prev) => ({
      ...prev,
      [provider.mobile_username]: 0,
    }));
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedProvider) return;

    const text = messageText.trim();
    const tempId = `temp-${Date.now()}`;

    const optimisticMsg = {
      id: tempId,
      message_content: text,
      created_date: new Date().toISOString(),
      status: "sending",
      isMine: true,
      senderType: "scribe",
      senderName: "You",
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    setMessageText("");

    try {
      const payload = {
        practice_id: userData.selected_practice,
        scribe_user_id: userData.email,
        provider_user_id: selectedProvider.mobile_username,
        scribe_name: `${userData.first_name} ${userData.last_name}`,
        message_content: text,
      };

      const res = await sendUserMessage(payload, token);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? {
                ...m,
                id: res.data?.id || m.id,
                status: "sent",
              }
            : m,
        ),
      );
    } catch (e) {
      console.error(e);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      showSnack("Failed to send message");
    }
  };

  const handleNext = () => {
    if (!matchedIndexes.length) return;

    setActiveMatchIndex((prev) =>
      prev === matchedIndexes.length - 1 ? 0 : prev + 1,
    );
  };

  const handlePrev = () => {
    if (!matchedIndexes.length) return;

    setActiveMatchIndex((prev) =>
      prev === 0 ? matchedIndexes.length - 1 : prev - 1,
    );
  };

  const clearSearch = () => {
    setSearchTerm("");
    setMatchedIndexes([]);
    setActiveMatchIndex(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "92vh",
        bgcolor: "#f0f2f5",
        overflow: "hidden",
      }}
    >
      <ProviderList
        providers={providers}
        loading={loadingProviders}
        selectedProvider={selectedProvider}
        onSelect={handleProviderSelect}
        theme={theme}
        messageCounts={messageCounts}
      />

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            p: 2,
            bgcolor: "#ededed",
            borderBottom: "1px solid #ddd",
            display: "flex",
            alignItems: "center",
          }}
        >
          {!isSearching && (
            <>
              <Typography fontWeight={600} sx={{ flex: 1 }}>
                {selectedProvider
                  ? selectedProvider.fullname
                  : "Select a provider to start chatting"}
              </Typography>

              {selectedProvider && (
                <IconButton onClick={() => setIsSearching(true)}>
                  <Search />
                </IconButton>
              )}
            </>
          )}

          {isSearching && (
            <>
              <TextField
                autoFocus
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: 1 }}
              />

              {searchTerm && matchedIndexes.length > 0 && (
                <Typography sx={{ mx: 1, fontSize: 14 }}>
                  {activeMatchIndex + 1}/{matchedIndexes.length}
                </Typography>
              )}

              {matchedIndexes.length > 1 && (
                <>
                  <IconButton size="small" onClick={handlePrev}>
                    <KeyboardArrowUp />
                  </IconButton>

                  <IconButton size="small" onClick={handleNext}>
                    <KeyboardArrowDown />
                  </IconButton>
                </>
              )}

              <IconButton
                size="small"
                onClick={() => {
                  setIsSearching(false);
                  clearSearch();
                }}
              >
                <Close />
              </IconButton>
            </>
          )}
        </Box>

        <Box
          ref={chatContainerRef}
          sx={{
            flex: 1,
            p: 2,
            overflowY: "auto",
            position: "relative",
            backgroundColor: "#f0f2f5",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: `url('${headerLogo}')`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "300px",
              opacity: 0.25,
              pointerEvents: "none",
            },
          }}
        >
          <ChatMessageList
            messages={messages}
            theme={theme}
            loading={loadingMessages}
            searchTerm={searchTerm}
            matchedIndexes={matchedIndexes}
            activeMatchIndex={activeMatchIndex}
          />
        </Box>

        <ChatInput
          messageText={messageText}
          setMessageText={setMessageText}
          onSend={handleSendMessage}
        />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack({ ...snack, open: false })}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScriberChatting;
